import StandardButton from "../UI/buttons/StandardButton";
import RadioButton from "../UI/RadioButton";
import labels from "../../utils/labels";
import useContingencySettings from "../../hooks/useContingencySettings";
import InputNumberField from "../UI/input/InputNumberField";
import { useAppDispatch } from "../../store/hooks";
import {
  clearGanttChartData,
  setIsContingencyMode,
} from "../../store/ganttChart";
import Checkbox from "../UI/input/Checkbox";
import ContingencySection from "./ContingencySection";
import ContingencySectionHeader from "./ContingencySectionHeader";
import Accordion from "../UI/Accordion";
import { useMemo, useState } from "react";
import useDataSelector from "../../hooks/useDataSelector";
import timestampService from "../../service/timestampService";
import { isReplanningShown } from "../../utils/environmentUtils";
import { ReplanningTimeOption } from "../../types/calendarSettings";

const pad = (n: number) => String(n).padStart(2, "0");

const formatTime = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

const buildTimeOptions = (
  now: Date,
  lastEndIso: string,
): ReplanningTimeOption[] => {
  const options: ReplanningTimeOption[] = [];
  const timeSlot = new Date(now);
  timeSlot.setMinutes(timeSlot.getMinutes() + 30);
  timeSlot.setMinutes(Math.ceil(timeSlot.getMinutes() / 30) * 30, 0, 0);
  const end = new Date(lastEndIso);
  while (timeSlot < end) {
    options.push({
      label: formatTime(timeSlot),
      freezeTimestamp: timeSlot.toISOString(),
    });
    timeSlot.setMinutes(timeSlot.getMinutes() + 30);
  }
  return options;
};

const ContingencySettingsTab = () => {
  const {
    minimumAffectedDuration,
    selectedAdjustmentStep,
    onAdjustmentStepSelect,
    handleRunContingency,
    setMinimumAffectedDuration,
    isReady,
    setExpandedTransportationTime,
    expandedTransportationTime,
    adjustmentOptions,
  } = useContingencySettings();

  const dispatch = useAppDispatch();
  const { dataToUse } = useDataSelector();
  const [planningStartTime, setPlanningStartTime] = useState<string>();

  const timeOptions = useMemo(() => {
    if (!dataToUse || dataToUse.employees.length === 0) return [];
    const firstShiftStart = dataToUse.employees
      .map((e) => e.shiftStartTime)
      .reduce((a, b) => (a < b ? a : b));
    const lastShiftEnd = dataToUse.employees
      .map((e) => e.shiftEndTime)
      .reduce((a, b) => (a > b ? a : b));
    // The plan's day comes from the first shift start: the last shift end can
    // fall after midnight. Only today's plan is limited to times still ahead.
    const dayStart = new Date(firstShiftStart);
    const now = new Date();
    const from =
      dayStart.toDateString() === now.toDateString() ? now : dayStart;
    return buildTimeOptions(from, lastShiftEnd);
  }, [dataToUse]);

  const isAdjustmentSelected = selectedAdjustmentStep !== null;

  return (
    <div
      className="flex flex-col h-full overflow-y-auto overflow-x-hidden"
      style={{ marginBottom: "20px" }}
    >
      <div
        className="p-[var(--padding-2xl)]"
        style={{ borderBottom: "1px solid var(--col-border-primary) " }}
      >
        <h2 style={{ font: "var(--font-h2)" }}>{labels.contingencyTitle}</h2>
        <p style={{ font: "var(--font-body-1)" }}>
          {labels.contingencyDescriptionBeta}
        </p>
      </div>
      <div
        className="flex flex-col gap-[var(--spacing-xxl)] m-[var(--padding-2xl)]"
        style={{ borderBottom: "1px solid var(--col-border-primary) " }}
      >
        <ContingencySection flex>
          <ContingencySectionHeader
            title={labels.capacityAdjustmentTitle}
            description={labels.capacityAdjustmentDescription}
          />
          <div className="flex flex-col gap-[var(--spacing-lg)]">
            <div className="flex flex-col gap-[var(--spacing-sm)]">
              <RadioButton
                label={labels.noAdjustment}
                checked={!selectedAdjustmentStep}
                onChange={() => onAdjustmentStepSelect(null)}
              />
            </div>
            {adjustmentOptions.map((option) => (
              <div
                key={option.id}
                className="flex flex-col gap-[var(--spacing-sm)]"
              >
                <RadioButton
                  label={option.label}
                  checked={selectedAdjustmentStep === option.id}
                  onChange={() => onAdjustmentStepSelect(option.id)}
                />
                <ul
                  className="list-disc list-inside flex gap-[14px] flex-col"
                  style={{ marginLeft: "25px" }}
                >
                  {option.lines.map((line) => (
                    <li className="label" key={line}>
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </ContingencySection>
        <ContingencySection>
          <InputNumberField
            id="contingency-minimum-visit-duration"
            minWidth="200px"
            direction="COLUMN"
            placeholder={labels.selectMinimumTime}
            max={60}
            min={0}
            label={labels.adjustVisitsOverMinimum}
            value={minimumAffectedDuration}
            onInputChange={(value) => setMinimumAffectedDuration(value)}
            tailingLabel="min."
          />
        </ContingencySection>{" "}
        {isReplanningShown && (
          <>
            <ContingencySectionHeader
              title={labels.replanFromTimeTitle}
              description={labels.replanFromTimeDescription}
            />
            <Accordion
              classes="min-w-[300px] w-[300px]"
              options={timeOptions}
              labelKey="label"
              variant="PRIMARY"
              onChange={(option): void =>
                setPlanningStartTime(option.freezeTimestamp)
              }
              defaultText="Vælg tidspunkt"
              label={labels.replanFromTimePlaceholder}
            ></Accordion>
          </>
        )}
        <ContingencySection className="mb-[var(--padding-2xl)]">
          <ContingencySectionHeader
            title={labels.transport}
            description={labels.expandTransportTimeDescription}
          />
          <Checkbox
            checked={expandedTransportationTime}
            onChange={setExpandedTransportationTime}
            label={labels.expandTransportTime}
          />
        </ContingencySection>
        {/*
        "Right column" is kept for future implementation
        if we want "replanlægning" to be part of the contingency settings

        Right column

        <div className="flex flex-col gap-4" style={{ flex: 1 }}>
          <h3 style={{ font: "var(--font-h3)" }}>
            Fjern arbejdstid for medarbejder
          </h3>
          <h4 style={{ font: "var(--font-bodyStd)", fontWeight: "bold" }}>
            Medarbejder
          </h4>
          <div
            className="flex flex-col gap-2"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            {loading ? (
              <Spinner />
            ) : employees.length > 0 ? (
              employees.map((employee) => (
                <label
                  key={employee.employeeRef}
                  className="flex items-center gap-2"
                  style={{ font: "var(--font-bodyStd)", cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={selectedEmployees.has(employee.employeeRef)}
                    onChange={() => toggleEmployee(employee.employeeRef)}
                  />
                  {employee.fullName}
                </label>
              ))
            ) : (
              <p style={{ color: "var(--col-text-secondary)" }}>
                Ingen medarbejdere tilgængelige.
              </p>
            )}
          </div>

          <p style={{ color: "var(--col-text-secondary)" }}>
            Nødplan starter fra
          </p>

          <div className="flex items-center gap-4">
            <select
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              style={{
                padding: "var(--spacing-sm) var(--spacing-md)",
                border: "1px solid var(--col-border-primary)",
                borderRadius: "var(--border-radius-minimal)",
                font: "var(--font-bodyStd)",
                outline: "none",
                cursor: "pointer",
                minWidth: "180px",
                color: selectedTime ? "inherit" : "var(--col-text-secondary)",
              }}
            >
              <option value="">Vælg tidspunkt</option>
              {timestamps.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>
        </div> */}
      </div>
      <div className="flex w-full items-end justify-start gap-[var(--spacing-sm)] ml-[var(--padding-2xl)]">
        <StandardButton
          width="200px"
          onClick={() => dispatch(setIsContingencyMode(false))}
          variant="SECONDARY"
          ariaLabel={labels.cancel}
        >
          {labels.cancel}
        </StandardButton>
        <StandardButton
          width="200px"
          onClick={(): Promise<void> => handleRunContingency(planningStartTime)}
          variant="PRIMARY"
          ariaLabel={labels.runContingency}
          disable={
            !isReady || (!isAdjustmentSelected && !expandedTransportationTime)
          }
        >
          {labels.runContingency}
        </StandardButton>
      </div>
    </div>
  );
};

export default ContingencySettingsTab;
